import type { ReactElement } from 'react';
import { createContext, useEffect, useMemo, useState } from 'react';
import type {
    AccountRequestApiResult,
    GetAccountRequestApproverForOrganizationApiResponse,
} from '@common/interfaces/api/accountRequestsApi';
import type { PractitionerMatch } from '@common/interfaces/practitionerMatchType';
import type { AccountRequestMatch } from '@common/validation/accountRequest';
import { AccountRequestStatus } from '@common/enums/accountRequestStatus';
import {
    approveAccountRequest as approveAccountRequestApi,
    getAccountRequestApproversForOrganization as getAccountRequestApproversForOrganizationApi,
    getMatchingPractitioners,
    getOneAccountRequest as getOneAccountRequestApi,
    rejectAccountRequest as rejectAccountRequestApi,
    updateAccountRequestApprover as updateAccountRequestApproverApi,
} from '@frontend/services/api/accountRequestsApiService';
import { hasAccountRequestAuthorization } from '@frontend/utils/hasAuthorization';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';

interface AccountRequestContext {
    accountRequest: AccountRequestApiResult;
    approveAccountRequest: () => Promise<AccountRequestApiResult>;
    approvers: GetAccountRequestApproverForOrganizationApiResponse['items'];
    fetchMatchingPractitioners: () => Promise<PractitionerMatch[]>;
    getScrollToId: (accountRequest: AccountRequestApiResult) => string | undefined;
    hasReviewAuthorizationError: boolean;
    isEditing: boolean;
    isLoading: boolean;
    needsKsyosApproval: boolean;
    rejectAccountRequest: (reason: string) => Promise<void>;
    selectedMatch: AccountRequestMatch | null;
    setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedMatch: React.Dispatch<React.SetStateAction<AccountRequestMatch | null>>;
    updateApprover: (approverId: string | null) => Promise<void>;
}

interface AccountRequestProviderProps {
    accountRequestId: string;
    children(renderProps: AccountRequestContext): ReactElement;
}

export const AccountRequestContext = createContext<AccountRequestContext>({} as AccountRequestContext);

export function AccountRequestProvider(props: Readonly<AccountRequestProviderProps>) {
    const abortSignal = useAbortSignal();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [hasReviewAuthorizationError, setHasReviewAuthorizationError] = useState<boolean>(false);

    const [accountRequest, setAccountRequest] = useState<AccountRequestContext['accountRequest']>(
        {} as AccountRequestContext['accountRequest']
    );
    const [approvers, setApprovers] = useState<AccountRequestContext['approvers']>([]);
    const [matchingPractitioners, setMatchingPractitioners] = useState<PractitionerMatch[]>([]);
    const [needsKsyosApproval, setNeedsKsyosApproval] = useState<boolean>(false);
    const [selectedMatch, setSelectedMatch] = useState<AccountRequestMatch | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);

            const nextAccountRequest = await fetchAccountRequest(props.accountRequestId);
            const nextApprovers = await fetchApprovers(nextAccountRequest.organizationId, nextAccountRequest.id);

            setApprovers(nextApprovers);
            setNeedsKsyosApproval(nextApprovers.length < 1 || nextAccountRequest.needsKsyosApproval);
            setHasReviewAuthorizationError(
                !hasAccountRequestAuthorization('review', false, nextAccountRequest.approverPractitionerId!)
            );

            setIsLoading(false);
        };

        fetchData();
    }, [props.accountRequestId]);

    const contextState = useMemo<AccountRequestContext>(
        () => ({
            accountRequest,
            approveAccountRequest,
            approvers,
            fetchMatchingPractitioners: () => fetchMatchingPractitioners(accountRequest),
            getScrollToId,
            hasReviewAuthorizationError,
            isEditing,
            isLoading,
            matchingPractitioners,
            needsKsyosApproval,
            rejectAccountRequest,
            selectedMatch,
            setIsEditing,
            setSelectedMatch,
            updateApprover,
        }),
        [
            accountRequest,
            approveAccountRequest,
            approvers,
            hasReviewAuthorizationError,
            isEditing,
            isLoading,
            needsKsyosApproval,
            rejectAccountRequest,
            setIsEditing,
            updateApprover,
        ]
    );

    return (
        <AccountRequestContext.Provider value={contextState}>
            {props.children(contextState)}
        </AccountRequestContext.Provider>
    );

    async function fetchAccountRequest(accountRequestId: string) {
        const nextAccountRequest = await getOneAccountRequestApi(accountRequestId, abortSignal);

        setAccountRequest(nextAccountRequest);

        return nextAccountRequest;
    }

    async function fetchApprovers(organizationId: string, accountRequestId?: string) {
        const approversItems = await getAccountRequestApproversForOrganizationApi(
            organizationId,
            accountRequestId,
            abortSignal
        );

        return approversItems.items;
    }

    async function fetchMatchingPractitioners(latestAccountRequest: AccountRequestApiResult) {
        const nextMatchingPractitioners = await getMatchingPractitioners(
            latestAccountRequest.userData.practitioner.nameFamily,
            latestAccountRequest.userData.practitioner.gender,
            abortSignal
        );

        const filteredMatchingPractitioners = nextMatchingPractitioners.filter(
            ({ accountRequestId }) => accountRequestId !== latestAccountRequest.id
        );

        setMatchingPractitioners(filteredMatchingPractitioners);

        return filteredMatchingPractitioners;
    }

    async function approveAccountRequest() {
        await approveAccountRequestApi(accountRequest.id, selectedMatch, abortSignal);

        return await fetchAccountRequest(accountRequest.id);
    }

    async function rejectAccountRequest(reason: string) {
        await rejectAccountRequestApi(accountRequest.id, reason, abortSignal);

        await fetchAccountRequest(accountRequest.id);
    }

    async function updateApprover(approverId: string | null) {
        await updateAccountRequestApproverApi({
            accountRequestId: accountRequest.id,
            approverId,
            needsKsyosApproval,
            signal: abortSignal,
        });

        await fetchAccountRequest(accountRequest.id);
    }

    function getScrollToId({ status, id, practitionerId }: AccountRequestApiResult) {
        if (status === AccountRequestStatus.approved) {
            return practitionerId!;
        }

        if (status === AccountRequestStatus.pending || status === AccountRequestStatus.rejected) {
            return id;
        }
    }
}
